import { Component, input, signal } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import {
  InlineButtonComponent,
  InlineButtonSize,
  InlineButtonVariant,
} from '../../atoms/inline-button/inline-button.component';
import { TooltipDirective } from '../../molecules/tooltip/tooltip.directive';
import { JoiiIcon } from '../../icons/lucide/icon';

export interface InlineButtonSelectionOption<TValue = unknown> {
  label: string;
  value: TValue;
  icon?: JoiiIcon;
  disabled?: boolean;
  /** When set, this text is shown as a tooltip while the option is hovered or focused */
  tooltip?: string;
}

@Component({
  selector: 'joii-inline-button-selection',
  imports: [InlineButtonComponent, TooltipDirective],
  providers: [provideValueAccessor(InlineButtonSelectionComponent)],
  host: {
    role: 'group',
    '[attr.aria-disabled]': 'disabled()',
    '(focusout)': 'onTouched?.()',
  },
  template: `
    @for (option of options(); track option.value) {
      <span class="option" [joiiTooltip]="option.tooltip ?? ''" tooltipShowDelay="0">
        <button
          joii-inline-button
          type="button"
          [size]="size()"
          [variant]="variant()"
          [prefixIcon]="option.icon"
          [active]="isSelected(option)"
          [disabled]="disabled() || option.disabled === true"
          [attr.aria-pressed]="isSelected(option)"
          (click)="selectOption(option)"
        >
          {{ option.label }}
        </button>
      </span>
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-3);
      flex-wrap: wrap;
    }

    /* Wrapper owns the tooltip trigger so tooltips still show for disabled options,
       which do not receive pointer events themselves. */
    .option {
      display: inline-flex;
    }
  `,
})
export class InlineButtonSelectionComponent implements ControlValueAccessor {
  readonly options = input.required<readonly InlineButtonSelectionOption[]>();
  readonly size = input<InlineButtonSize>('md');
  readonly variant = input<InlineButtonVariant>('secondary');

  protected readonly disabled = signal(false);
  private readonly selectedValue = signal<unknown>(undefined);
  private onChange?: ChangeFn<unknown>;
  protected onTouched?: TouchedFn;

  writeValue(value: unknown): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: ChangeFn<unknown>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected isSelected(option: InlineButtonSelectionOption): boolean {
    return Object.is(this.selectedValue(), option.value);
  }

  protected selectOption(option: InlineButtonSelectionOption): void {
    if (this.disabled() || option.disabled === true) {
      return;
    }

    this.selectedValue.set(option.value);
    this.onChange?.(option.value);
    this.onTouched?.();
  }
}
